import { BookPlus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useTranslation } from "@/hooks/useTranslation";
import type { StrengthSession, StrengthSet } from "../types";

interface StrengthHistoryTableProps {
  sessions: StrengthSession[];
  selectedExerciseId?: number;
  selectedTimedGroupIds: ReadonlySet<number>;
  canWrite: boolean;
  onToggleTimedGroup: (groupId: number, selected: boolean) => void;
  onSelectAllTimedGroups: () => void;
  onClearTimedGroups: () => void;
  onEdit: (session: StrengthSession) => void;
  onDelete: (session: StrengthSession) => void;
  onSaveTemplate?: (session: StrengthSession) => void;
}

function SetList({ sets }: { sets: StrengthSet[] }) {
  const { i18n } = useTranslation();
  return (
    <ul className="space-y-1 text-sm">
      {sets.map(set => (
        <li key={set.id}>
          <span className="font-medium">{set.cvikNazov}</span> ·{" "}
          {set.opakovania} ×{" "}
          {Number(set.hmotnostKg).toLocaleString(i18n.language)} kg
        </li>
      ))}
    </ul>
  );
}

export function StrengthHistoryTable({
  sessions,
  selectedExerciseId,
  selectedTimedGroupIds,
  canWrite,
  onToggleTimedGroup,
  onSelectAllTimedGroups,
  onClearTimedGroups,
  onEdit,
  onDelete,
  onSaveTemplate,
}: StrengthHistoryTableProps) {
  const { t, i18n } = useTranslation();
  const visibleSessions = sessions
    .map(session => {
      const matchingSets = selectedExerciseId
        ? session.serie.filter(set => set.cvikId === selectedExerciseId)
        : session.serie;
      const timedGroups = (session.timedGroups ?? []).filter(
        group => !selectedExerciseId || group.cvikId === selectedExerciseId
      );
      return { session, matchingSets, timedGroups };
    })
    .filter(({ matchingSets }) => matchingSets.length > 0);
  const timedGroupIds = visibleSessions.flatMap(({ timedGroups }) =>
    timedGroups.map(group => group.id)
  );
  const formatDate = (value: string) =>
    new Intl.DateTimeFormat(i18n.language, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(`${value}T00:00:00`));
  const formatDuration = (seconds: number) =>
    `${seconds.toLocaleString(i18n.language)} s`;

  return (
    <Card>
      <CardHeader className="gap-3 sm:flex-row sm:items-center sm:justify-between">
        <CardTitle>{t("strengthProgress:history.title")}</CardTitle>
        {timedGroupIds.length > 0 && (
          <div className="flex gap-2">
            <Button
              size="sm"
              type="button"
              variant="outline"
              onClick={onSelectAllTimedGroups}
            >
              {t("strengthProgress:history.selectAll")}
            </Button>
            <Button
              size="sm"
              type="button"
              variant="outline"
              onClick={onClearTimedGroups}
            >
              {t("strengthProgress:history.clearSelection")}
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {visibleSessions.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {t("strengthProgress:history.empty")}
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("strengthProgress:history.date")}</TableHead>
                <TableHead>{t("strengthProgress:history.sets")}</TableHead>
                <TableHead>{t("strengthProgress:history.note")}</TableHead>
                {canWrite && (
                  <TableHead className="text-right">
                    {t("strengthProgress:history.actions")}
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleSessions.map(({ session, matchingSets, timedGroups }) => {
                const timedSetIds = new Set(
                  timedGroups.flatMap(group =>
                    matchingSets
                      .filter(set => set.casovaSkupinaId === group.id)
                      .map(set => set.id)
                  )
                );
                const ungroupedSets = matchingSets.filter(
                  set => !timedSetIds.has(set.id)
                );
                return (
                  <TableRow key={session.id}>
                    <TableCell className="whitespace-nowrap font-medium">
                      {formatDate(session.datum)}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-3">
                        {timedGroups.map(group => {
                          const groupSets = matchingSets.filter(
                            set => set.casovaSkupinaId === group.id
                          );
                          if (groupSets.length === 0) return null;
                          const checkboxId = `history-group-${group.id}`;
                          return (
                            <div
                              className="rounded-md border border-muted p-2"
                              key={group.id}
                            >
                              <div className="mb-2 flex items-center gap-2 text-sm font-medium">
                                <Checkbox
                                  id={checkboxId}
                                  aria-label={`${t("strengthProgress:history.selectForChart")}: ${groupSets[0]!.cvikNazov}, ${formatDate(session.datum)}, ${formatDuration(group.trvanieSekund)}`}
                                  checked={selectedTimedGroupIds.has(group.id)}
                                  onCheckedChange={checked =>
                                    onToggleTimedGroup(
                                      group.id,
                                      checked === true
                                    )
                                  }
                                />
                                <label
                                  className="cursor-pointer"
                                  htmlFor={checkboxId}
                                >
                                  {t("strengthProgress:history.timedGroup")}
                                </label>
                                <span className="text-muted-foreground">
                                  · {formatDuration(group.trvanieSekund)}
                                </span>
                              </div>
                              <SetList sets={groupSets} />
                            </div>
                          );
                        })}
                        {ungroupedSets.length > 0 && (
                          <div className="rounded-md border border-dashed p-2">
                            <p className="mb-2 text-xs text-muted-foreground">
                              {t("strengthProgress:history.untimed")}
                            </p>
                            <SetList sets={ungroupedSets} />
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-xs whitespace-pre-wrap text-muted-foreground">
                      {session.poznamka || "—"}
                    </TableCell>
                    {canWrite && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {onSaveTemplate && (
                            <Button
                              aria-label={t(
                                "strengthProgress:templates.saveSession"
                              )}
                              title={t(
                                "strengthProgress:templates.saveSession"
                              )}
                              size="icon"
                              type="button"
                              variant="ghost"
                              onClick={() => onSaveTemplate(session)}
                            >
                              <BookPlus className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            aria-label={t("common:edit")}
                            size="icon"
                            type="button"
                            variant="ghost"
                            onClick={() => onEdit(session)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            aria-label={t("common:delete")}
                            size="icon"
                            type="button"
                            variant="ghost"
                            onClick={() => onDelete(session)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
